import { IconArrowsDiagonal, IconBabyCarriage, IconBuildingStore, IconArrowsExchange, IconAddressBook, IconBriefcase, IconCertificate, IconClipboardList, IconEdit, IconFileDescription, IconFileTypePdf, IconUserPlus, IconUserCheck, IconLayoutGrid, IconMap2, IconPhoto, IconReceipt2, IconPrinter, IconReportMoney, IconRouteSquare, IconKeyboard, IconSchool, IconTextSize, IconVideo, IconWand } from '@tabler/icons-react';
import megamenuData from '../../../../megamenu.json';

export const SITE_URL = 'https://sohozkaj.com';

export const toSlug = (path) => (path || '').replace(/^\/tools\//, '');

// A tool wears the "New" badge for this many days after its `addedAt` date. When you add a tool, give it
// `"addedAt": "YYYY-MM-DD"` in megamenu.json — it is new automatically and stops being new on its own.
// (A tool with no date is never new; a date in the future waits for its day.)
export const NEW_WINDOW_DAYS = 30;
export const isNewTool = (tool) => {
    if (!tool?.addedAt) return false;
    const ageDays = (Date.now() - new Date(`${tool.addedAt}T00:00:00`).getTime()) / 86400000;
    return ageDays >= 0 && ageDays <= NEW_WINDOW_DAYS;
};

// Tool icons are served from R2 (files.sohozkaj.com/icons/...). Map the catalog
// paths ("/tools-icon/...") onto that CDN. Category headers live at
// "icons/<cat>-tools.svg" and per-tool icons at "icons/<cat>/<n>.svg" — except
// the "general/" tool folder, which is named "general-tools/" on R2.
const ICON_CDN_BASE = 'https://files.sohozkaj.com/icons';
export const toolIconSrc = (path) =>
    !path
        ? ''
        : `${ICON_CDN_BASE}/${path.replace(/^\/tools-icon\//, '').replace(/^general\//, 'general-tools/')}`;

export const CATS = {
    result: { chip: 'bg-teal-50 dark:bg-teal-900/20', Icon: IconSchool, accent: 'text-teal-600 dark:text-teal-400', badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
    pdf: {
        chip: 'bg-pdf-cat-tint text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400',
        Icon: IconFileTypePdf,
        accent: 'text-pdf-cat-solid dark:text-orange-400',
        badge: 'bg-pdf-cat-tint text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400',
    },
    image: { chip: 'bg-amber-50 dark:bg-amber-900/20', Icon: IconPhoto, accent: 'text-amber-500 dark:text-amber-400', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    text: { chip: 'bg-sky-50 dark:bg-sky-900/20', Icon: IconTextSize, accent: 'text-sky-500 dark:text-sky-400', badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
    video: { chip: 'bg-violet-50 dark:bg-violet-900/20', Icon: IconVideo, accent: 'text-violet-500 dark:text-violet-400', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
    general: { chip: 'bg-purple-50 dark:bg-purple-900/20', Icon: IconClipboardList, accent: 'text-purple-500 dark:text-purple-400', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
};

/**
 * Category of the tool a route belongs to — `/tools/image/compress` → `image`.
 * Shared tool chrome (preview panel, result cards) uses it so an image tool shows
 * the image icon and an image tint, not the PDF one. Routes with no category
 * segment (`/tools/form-auto-fillup`) keep the PDF look they already had.
 */
export const categoryIdFromPath = (pathname = '') => {
    const parts = pathname.replace(/^\/+/, '').split('/');
    const i = parts.indexOf('tools');
    const id = i >= 0 ? parts[i + 1] : undefined;
    return id && CATS[id] ? id : 'pdf';
};

/** CATS entry (Icon/chip/accent/badge) for a route. Never undefined. */
export const catFromPath = (pathname) => CATS[categoryIdFromPath(pathname)];

const AUTO_FILLUP = {
    id: 'sohozJob',
    internal: true,
    path: '/tools/form-auto-fillup',
    name: 'Form auto fillup',
    nameBn: 'ফর্ম অটো ফিলআপ',
    categoryId: 'result',
    IconComp: IconBriefcase,
    addedAt: '2026-06-28', // first commit of the auto-fillup module
};

// Tabler icon overrides for tools whose CDN icon is missing/broken. Setting an
// IconComp and clearing iconImg makes the grid render the vector icon (see the
// iconImg ? <img> : <tool.IconComp> branch in Tools.jsx).
export const ICON_OVERRIDES = {
    'form-auto-fillup': IconBriefcase,
    'result/education-board-result': IconCertificate,
    'result/national-university-result': IconSchool,
    'general/hisabpottro': IconReportMoney,
    'general/bhata-tracking': IconRouteSquare,
    'general/typing-practice': IconKeyboard,
    'general/voter-migration-form': IconArrowsExchange,
    'general/nid-information-correction-form': IconAddressBook,
    'general/special-information-form': IconFileDescription,
    'general/voter-registration-form': IconUserPlus,
    'general/voter-inclusion-form': IconUserCheck,
    'general/embassy-birth-registration-form': IconBabyCarriage,
    'general/udyog-application-form': IconBuildingStore,
    'general/bangladesh-travel-map': IconMap2,
    'general/invoice-designer': IconReceipt2,
    'image/document-cleanup': IconWand,
    // No CDN icon for the upscaler yet — the vector keeps the grid consistent.
    'image/upscale': IconArrowsDiagonal,
    // No CDN icon for the notice generator yet — the vector keeps the grid consistent.
    'image/notice-generator': IconClipboardList,
    // No CDN icon for the editor yet — the vector keeps the grid consistent.
    'pdf/edit': IconEdit,
    // No CDN icon for the print-ready tool yet — the vector keeps the grid consistent.
    'image/print-ready-maker': IconPrinter,
};

// Tools that are announced but not yet built — rendered with a "Soon" badge in
// the grid and routed to the ComingSoon placeholder page.
export const SOON_SLUGS = new Set([
    'general/hisabpottro',
]);

// The tools that cost credits: they call the AI on the server, so they cannot be
// free the way a tool that runs in the browser is. They fill the "Paid" tab of
// the tools grid and are kept out of the free one. Each has a free counterpart
// that does the same job by hand, which links across to it.
export const PAID_SLUGS = new Set([
    'image/ai-photo-split',
    'text/ai-image-to-text',
]);

export const isPaidTool = (tool) => PAID_SLUGS.has(tool.slug);

const MENU_TOOLS = megamenuData.categories.flatMap((cat) =>
    cat.tools.map((tool) => {
        const slug = toSlug(tool.path);
        const IconComp = ICON_OVERRIDES[slug];
        return {
            ...tool,
            ...(IconComp ? { IconComp, iconImg: undefined } : {}),
            id: tool.path,
            slug,
            categoryId: cat.id,
            categoryTitle: cat.title,
            categoryTitleBn: cat.titleBn,
        };
    }),
);

// Tools pinned to the front of the /tools grid (after the auto-fillup tool),
// in this order. Everything else follows in megamenu order.
const FEATURED_SLUGS = ['result/education-board-result'];
const featured = FEATURED_SLUGS.map((slug) => MENU_TOOLS.find((t) => t.slug === slug)).filter(Boolean);
// Exclude the auto-fillup slug here — it is prepended explicitly (below) so it
// stays first in the grid; without this filter it would appear twice now that
// it also lives in megamenu.json (for the megamenu dropdown).
const rest = MENU_TOOLS.filter((t) => !FEATURED_SLUGS.includes(t.slug) && t.slug !== 'form-auto-fillup');

export const ALL_TOOLS = [{ ...AUTO_FILLUP, slug: toSlug(AUTO_FILLUP.path) }, ...featured, ...rest];

export const INTERNAL_SLUGS = new Set([
    'form-auto-fillup',
    'result/education-board-result',
    'result/national-university-result',
    'general/hisabpottro',
    'image/compress',
    'image/document-cleanup',
    'image/print-ready-maker',
    'image/nid-to-pdf',
    'image/photo-split',
    'image/ai-photo-split',
    'image/remove-background',
    'image/govt-job-photo-sign',
    'image/passport-to-pdf',
    'image/convert',
    'image/qr-code',
    'image/resize',
    'image/crop',
    'image/to-pdf',
    'image/watermark',
    'image/flatten',
    'image/barcode',
    'image/color-picker',
    'image/signature-pad',
    'image/signature-from-text',
    'image/logo-color-changer',
    'image/compress-gif',
    'image/upscale',
    'image/notice-generator',
    'text/bijoy-unicode',
    'text/banglish-typing',
    'text/number-to-words',
    'text/image-to-text',
    'text/ai-image-to-text',
    'text/text-analyzer',
    'text/date-format',
    'pdf/compress',
    'pdf/merge',
    'pdf/create',
    'pdf/split',
    'pdf/pdf-to-image',
    'pdf/protect',
    'pdf/rotate',
    'pdf/remove-pages',
    'pdf/watermark',
    'pdf/extract-images',
    'pdf/permissions',
    'pdf/resize',
    'pdf/crop',
    'pdf/pdf-to-excel',
    // 'pdf/pdf-to-word', // disabled — see pdf/pdf-to-word/PdfToWord.jsx to re-enable
    'pdf/repair',
    'pdf/edit',
    'video/compress',
    'video/trim',
    'video/merge',
    'video/convert',
    'video/watermark',
    'video/volume',
    'video/speed',
    'video/to-gif',
    'video/rotate',
    'video/remove-audio',
    'general/family-card-form',
    'general/voter-migration-form',
    'general/nid-information-correction-form',
    'general/special-information-form',
    'general/voter-registration-form',
    'general/voter-inclusion-form',
    'general/embassy-birth-registration-form',
    'general/udyog-application-form',
    'general/bhata-tracking',
    'general/fuel-card-form',
    'general/ats-cv-maker',
    'general/age-calculator',
    'general/emi-calculator',
    'general/bmi-calculator',
    'general/typing-practice',
    'general/bangladesh-travel-map',
    'general/invoice-designer',
]);

export const isInternalTool = (tool) => tool.internal || INTERNAL_SLUGS.has(tool.slug);

export function getToolBySlug(slug) {
    return ALL_TOOLS.find((tool) => tool.slug === slug) || null;
}

/**
 * Tools shown under a tool page. Same category first; a small category (result
 * has five tools) is topped up from the rest of the catalogue so the row always
 * fills its `limit`. Unbuilt ("soon") tools never pad — the row would send the
 * visitor to a placeholder page.
 */
export function getRelatedTools(slug, limit = 6) {
    const current = getToolBySlug(slug);
    if (!current) return [];
    const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
    const sameCategory = ALL_TOOLS
        .filter((tool) => tool.categoryId === current.categoryId && tool.slug !== slug)
        .sort(byOrder);
    if (sameCategory.length >= limit) return sameCategory.slice(0, limit);
    const filler = ALL_TOOLS
        .filter((tool) => tool.categoryId !== current.categoryId && !SOON_SLUGS.has(tool.slug))
        .sort(byOrder);
    return [...sameCategory, ...filler].slice(0, limit);
}

export const CATEGORY_FILTERS = megamenuData.categories.map((cat) => ({
    key: cat.id,
    title: cat.title,
    titleBn: cat.titleBn,
    Icon: (CATS[cat.id] || {}).Icon || IconLayoutGrid,
}));

export const TOOL_TAB_KEYS = ['all', 'fav', 'new', 'hot', ...CATEGORY_FILTERS.map((f) => f.key)];

export function isValidToolTab(tab) {
    return TOOL_TAB_KEYS.includes(tab);
}

export function getCategoryFilter(key) {
    return CATEGORY_FILTERS.find((f) => f.key === key) || null;
}
